import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Avatar,
  Badge,
  Box,
  Button,
  ButtonGroup,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Spinner,
  Switch,
  Text,
  VStack,
} from '@chakra-ui/react'
import { Forward, Reply, ReplyAll, Search } from 'lucide-react'
import { EmailRichComposer } from '@/components/email/EmailRichComposer'
import { useThemeColors } from '@/hooks/useThemeColors'
import {
  getEmailDetail,
  getEmailThreads,
  forwardEmail,
  getInboxEmails,
  markEmailAsRead,
  replyToEmail,
  type EmailThread,
  type ReceivedEmail,
} from '@/lib/services/inboxService'
import { isEmptyHtml } from '@/lib/utils/emailAttachments'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { getAvatarColorFromName } from '@/lib/utils/avatarColor'
import { useInboxNotificationsStore } from '@/store/inboxNotifications'
import { invalidRecipientMessage, sanitizeEmailHtml } from '@/lib/utils/emailSafety'

type ViewMode = 'emails' | 'threads'
type ComposerMode = 'reply' | 'replyAll' | 'forward' | null
const INBOX_PER_PAGE = 20

function formatReceivedShort(value: string | null | undefined): string {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return String(value)
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  let hr = d.getHours()
  const min = String(d.getMinutes()).padStart(2, '0')
  const ampm = hr >= 12 ? 'pm' : 'am'
  hr = hr % 12 || 12
  return `${mm}/${dd} ${hr}:${min} ${ampm}`
}

function formatReceivedFull(value: string | null | undefined): string {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return String(value)
  return d.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function UnreadBadge({ count }: { count: number }) {
  if (!count || count <= 0) return null
  return (
    <Badge
      colorScheme="red"
      variant="solid"
      borderRadius="full"
      minW="20px"
      h="20px"
      display="flex"
      alignItems="center"
      justifyContent="center"
      fontSize="xs"
      px="2"
    >
      {count > 99 ? '99+' : count}
    </Badge>
  )
}

function buildReplySubject(subject: string | null): string {
  const base = (subject || '').trim()
  if (!base) return 'Re: (No subject)'
  return /^re:\s*/i.test(base) ? base : `Re: ${base}`
}

function buildForwardSubject(subject: string | null): string {
  const base = (subject || '').trim()
  if (!base) return 'Fwd: (No subject)'
  return /^fwd?:\s*/i.test(base) ? base : `Fwd: ${base}`
}

function escapeHtmlText(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function buildQuotedReply(email: ReceivedEmail): string {
  const fromName = (email.from_name || '').trim()
  const fromEmail = (email.from_email || '').trim()
  const fromLabel = fromName && fromEmail ? `${fromName} <${fromEmail}>` : fromName || fromEmail || 'Unknown sender'
  const sent = formatReceivedFull(email.received_at)
  const subject = (email.subject || '(No subject)').trim()
  const plainBody = (email.body || '')
    .replace(/<style[\s\S]*?<\/style>/gi, '')
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<\/div>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .trim()

  const quotedLines = [
    '------------------------------',
    `From: ${fromLabel}`,
    `Sent: ${sent}`,
    'To:',
    `Subject: ${subject}`,
    '',
    plainBody || '(No original body)',
  ]
    .map((line) => `<p>${escapeHtmlText(line) || '<br>'}</p>`)
    .join('')

  return `<p></p><p></p>${quotedLines}`
}

function normalizeEmailHtml(html: string | null | undefined): string {
  const source = sanitizeEmailHtml(html || '').trim()
  if (!source) return ''

  // Fix protocol-relative URLs (e.g. //image.host/path) used by some email providers.
  let normalized = source.replace(/(src|href)=["']\/\/([^"']+)["']/gi, '$1="https://$2"')

  // Keep images responsive inside the iframe viewport.
  const responsiveStyles =
    '<style>img{max-width:100%;height:auto;}table{max-width:100%;}body{margin:0;padding:12px;box-sizing:border-box;}</style>'

  if (/<head[^>]*>/i.test(normalized)) {
    normalized = normalized.replace(/<head[^>]*>/i, (match) => `${match}${responsiveStyles}`)
  } else {
    normalized = `<html><head>${responsiveStyles}</head><body>${normalized}</body></html>`
  }

  return normalized
}

export default function MailboxInbox() {
  const colors = useThemeColors()
  const { showError, showSuccess } = useToastNotification()
  const setInboxMeta = useInboxNotificationsStore((s) => s.setMeta)
  const inboxTopId = useInboxNotificationsStore((s) => s.topId)

  const [viewMode, setViewMode] = useState<ViewMode>('emails')
  const [emails, setEmails] = useState<ReceivedEmail[]>([])
  const [threads, setThreads] = useState<EmailThread[]>([])
  const [loading, setLoading] = useState(true)
  const [emailsPage, setEmailsPage] = useState(1)
  const [emailsHasMore, setEmailsHasMore] = useState(false)
  const [emailsLoadingMore, setEmailsLoadingMore] = useState(false)
  const [detailLoading, setDetailLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [unreadOnly, setUnreadOnly] = useState(false)
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [selectedEmail, setSelectedEmail] = useState<ReceivedEmail | null>(null)
  const [expandedThreadKeys, setExpandedThreadKeys] = useState<Set<string>>(new Set())
  const [composerMode, setComposerMode] = useState<ComposerMode>(null)
  const [replyBody, setReplyBody] = useState('')
  const [replyAttachments, setReplyAttachments] = useState<File[]>([])
  const [replyCc, setReplyCc] = useState('')
  const [replySending, setReplySending] = useState(false)
  const [forwardTo, setForwardTo] = useState('')
  const [emailBodyHeight, setEmailBodyHeight] = useState(360)

  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const bodyTopRef = useRef<HTMLDivElement | null>(null)
  const detailScrollRef = useRef<HTMLDivElement | null>(null)
  const emailBodyBoxRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current)
    searchDebounceRef.current = setTimeout(() => setDebouncedSearch(search.trim()), 350)
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current)
    }
  }, [search])

  useEffect(() => {
    bodyTopRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [selectedId, selectedEmail?.id])

  const syncInboxNotificationsPeek = useCallback(async () => {
    try {
      const res = await getInboxEmails({ per_page: 1, page: 1 })
      const latest = res.data[0]
      setInboxMeta(res.meta.unread_count, res.meta.total, latest?.id ?? null)
    } catch {
      /* ignore */
    }
  }, [setInboxMeta])

  const loadList = useCallback(
    async (opts?: { silent?: boolean }) => {
      try {
        if (!opts?.silent) setLoading(true)

        if (viewMode === 'threads') {
          const res = await getEmailThreads({
            search: debouncedSearch || undefined,
            is_read: unreadOnly ? '0' : undefined,
          })
          setThreads(res.data)
          setEmailsHasMore(false)
          setEmailsPage(1)
        } else {
          const res = await getInboxEmails({
            page: 1,
            per_page: INBOX_PER_PAGE,
            search: debouncedSearch || undefined,
            is_read: unreadOnly ? '0' : undefined,
          })
          setEmails(res.data)
          setEmailsPage(res.meta.current_page)
          setEmailsHasMore(res.meta.current_page < res.meta.last_page)
          const isUnfiltered = !unreadOnly && !debouncedSearch.trim()
          if (isUnfiltered) {
            const topId = res.data[0]?.id ?? null
            setInboxMeta(res.meta.unread_count, res.meta.total, topId)
          }
        }
      } catch (e) {
        showError(e)
      } finally {
        if (!opts?.silent) setLoading(false)
      }
    },
    [debouncedSearch, unreadOnly, viewMode, showError, setInboxMeta],
  )

  const loadMoreEmails = useCallback(async () => {
    if (viewMode !== 'emails') return
    if (loading || emailsLoadingMore || !emailsHasMore) return

    try {
      setEmailsLoadingMore(true)
      const nextPage = emailsPage + 1
      const res = await getInboxEmails({
        page: nextPage,
        per_page: INBOX_PER_PAGE,
        search: debouncedSearch || undefined,
        is_read: unreadOnly ? '0' : undefined,
      })

      setEmails((prev) => {
        const existingIds = new Set(prev.map((email) => email.id))
        const appended = res.data.filter((email) => !existingIds.has(email.id))
        return [...prev, ...appended]
      })
      setEmailsPage(res.meta.current_page)
      setEmailsHasMore(res.meta.current_page < res.meta.last_page)
    } catch (e) {
      showError(e)
    } finally {
      setEmailsLoadingMore(false)
    }
  }, [
    debouncedSearch,
    emailsHasMore,
    emailsLoadingMore,
    emailsPage,
    loading,
    showError,
    unreadOnly,
    viewMode,
  ])

  const handleListScroll = useCallback(
    (e: { currentTarget: HTMLDivElement }) => {
      if (viewMode !== 'emails') return
      const target = e.currentTarget
      const remaining = target.scrollHeight - target.scrollTop - target.clientHeight
      if (remaining <= 120) {
        void loadMoreEmails()
      }
    },
    [loadMoreEmails, viewMode],
  )

  useEffect(() => {
    void loadList()
  }, [loadList])

  useEffect(() => {
    if (inboxTopId === null) return
    void loadList({ silent: true })
  }, [inboxTopId, loadList])

  const openEmail = async (id: number) => {
    setSelectedId(id)
    setComposerMode(null)
    setReplyBody('')
    setReplyAttachments([])
    setReplyCc('')
    setForwardTo('')
    setDetailLoading(true)
    try {
      const detail = await getEmailDetail(id)
      setSelectedEmail(detail)
      setEmails((prev) =>
        prev.map((e) => (e.id === id ? { ...e, is_read: true } : e)),
      )
      await loadList({ silent: true })
      void syncInboxNotificationsPeek()
    } catch (e) {
      showError(e)
    } finally {
      setDetailLoading(false)
    }
  }

  const handleMarkRead = async () => {
    if (!selectedId) return
    try {
      await markEmailAsRead(selectedId)
      setSelectedEmail((e) => (e ? { ...e, is_read: true } : null))
      setEmails((prev) =>
        prev.map((e) => (e.id === selectedId ? { ...e, is_read: true } : e)),
      )
      showSuccess('Marked as read')
      await loadList({ silent: true })
      void syncInboxNotificationsPeek()
    } catch (e) {
      showError(e)
    }
  }

  const handleSendReply = async () => {
    if (!selectedId || !selectedEmail || isEmptyHtml(replyBody)) return
    try {
      setReplySending(true)
      const cleanCc = replyCc.trim() || undefined
      if (composerMode === 'forward') {
        if (!forwardTo.trim()) {
          showError('Recipient email is required for forward.')
          return
        }
        const recipientProblem = invalidRecipientMessage(forwardTo, 'Recipient')
        if (recipientProblem) {
          showError(recipientProblem)
          return
        }
      }
      const ccProblem = invalidRecipientMessage(replyCc)
      if (ccProblem) {
        showError(ccProblem)
        return
      }
      if (composerMode === 'forward') {
        await forwardEmail(selectedId, forwardTo.trim(), replyBody, cleanCc, replyAttachments)
        showSuccess('Forward sent successfully')
      } else {
        await replyToEmail(selectedId, replyBody, cleanCc, replyAttachments)
        showSuccess(composerMode === 'replyAll' ? 'Reply all sent successfully' : 'Reply sent successfully')
      }
      setReplyBody('')
      setReplyAttachments([])
      setReplyCc('')
      setForwardTo('')
      setComposerMode(null)
      await handleMarkRead()
    } catch (e) {
      showError(e)
    } finally {
      setReplySending(false)
    }
  }

  const focusStyles = {
    borderColor: colors.inputBorder,
    boxShadow: 'none',
  }

  const senderLabel = (e: ReceivedEmail) =>
    e.from_name || e.from_email || 'Unknown sender'
  const getThreadKey = (thread: EmailThread) =>
    `${thread.thread_subject || '(No subject)'}-${thread.latest_id ?? 'none'}`
  const toggleThreadExpansion = (key: string) => {
    setExpandedThreadKeys((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const selectedCanReply = Boolean(selectedEmail?.from_email)
  const composerOpen = Boolean(composerMode)
  const replySubject = useMemo(
    () => buildReplySubject(selectedEmail?.subject || null),
    [selectedEmail?.subject],
  )
  const forwardSubject = useMemo(
    () => buildForwardSubject(selectedEmail?.subject || null),
    [selectedEmail?.subject],
  )

  useEffect(() => {
    const updateEmailBodyHeight = () => {
      const scrollEl = detailScrollRef.current
      const bodyBoxEl = emailBodyBoxRef.current
      if (!scrollEl || !bodyBoxEl) return

      const topWithinViewport = bodyBoxEl.offsetTop - scrollEl.scrollTop
      const remaining = scrollEl.clientHeight - topWithinViewport - 16
      setEmailBodyHeight(Math.max(260, Math.floor(remaining)))
    }

    const rafId = window.requestAnimationFrame(updateEmailBodyHeight)
    window.addEventListener('resize', updateEmailBodyHeight)

    return () => {
      window.cancelAnimationFrame(rafId)
      window.removeEventListener('resize', updateEmailBodyHeight)
    }
  }, [selectedId, selectedEmail?.id, composerOpen, detailLoading, replyBody, replyCc, forwardTo, replyAttachments.length])

  return (
    <Box position="absolute" inset={0} minW={0} minH={0} overflow="hidden" bg={colors.cardBg}>
      <Grid
        templateColumns={{ base: '1fr', lg: '300px 1fr' }}
        gap={0}
        h="full"
        minW={0}
        minH={0}
        overflow="hidden"
        borderTop="1px solid"
        borderTopColor={colors.borderLight}
      >
        <GridItem
          minW={0}
          minH={0}
          overflow="hidden"
          bg={colors.cardBg}
          borderRightWidth={{ base: 0, lg: '1px' }}
          borderRightStyle="solid"
          borderRightColor={colors.border}
          display="flex"
          flexDirection="column"
        >
          <Box
            p="16px"
            borderBottom="1px solid"
            borderColor={colors.borderLight}
            bg={colors.cardBg}
            flexShrink={0}
          >
            <Text fontSize="xs" color={colors.textMuted} mb={2}>
              Mailbox Inbox · List syncs with header notifications (15s).
            </Text>
            <ButtonGroup size="xs" isAttached variant="outline" mb={3}>
              <Button
                onClick={() => setViewMode('emails')}
                bg={viewMode === 'emails' ? colors.uploadHover : 'transparent'}
              >
                Emails
              </Button>
              <Button
                onClick={() => setViewMode('threads')}
                bg={viewMode === 'threads' ? colors.uploadHover : 'transparent'}
              >
                Threads
              </Button>
            </ButtonGroup>
            <FormControl display="flex" alignItems="center" mb={3}>
              <FormLabel mb={0} mr={2} fontSize="sm" color={colors.textMuted} flexShrink={0}>
                Unread only
              </FormLabel>
              <Switch
                isChecked={unreadOnly}
                onChange={(e) => setUnreadOnly(e.target.checked)}
                colorScheme="blue"
                size="sm"
              />
            </FormControl>
            <InputGroup>
              <InputLeftElement
                pointerEvents="none"
                h="100%"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Box as="span" color={colors.textMuted} lineHeight={0} display="flex">
                  <Search size={16} strokeWidth={2} aria-hidden />
                </Box>
              </InputLeftElement>
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search subject, sender…"
                size="sm"
                borderRadius="8px"
                bg={colors.inputBg}
                borderColor={colors.inputBorder}
                pl="36px"
                _hover={{ borderColor: colors.inputborderHover }}
                _focusVisible={focusStyles}
              />
            </InputGroup>
          </Box>

          <Box
            flex="1"
            minH={0}
            overflowY="auto"
            overscrollBehaviorY="contain"
            onScroll={handleListScroll}
          >
            {loading ? (
              <Flex justify="center" py={8}>
                <Spinner />
              </Flex>
            ) : viewMode === 'threads' ? (
              threads.length === 0 ? (
                <Text color={colors.textMuted} fontSize="sm" px={4} py={6}>
                  No threads found.
                </Text>
              ) : (
                <VStack spacing={0} align="stretch">
                  {threads.map((thread) => {
                    const latest = thread.emails[0]
                    const id = thread.latest_id
                    const name = latest ? senderLabel(latest) : thread.latest_from || 'Unknown sender'
                    const threadKey = getThreadKey(thread)
                    const isExpanded = expandedThreadKeys.has(threadKey)
                    return (
                      <Box key={threadKey} borderBottom="1px solid" borderColor={colors.borderLight}>
                        <Flex
                          px="14px"
                          py="14px"
                          gap="12px"
                          cursor={id ? 'pointer' : 'default'}
                          bg={selectedId === id ? colors.uploadHover : colors.cardBg}
                          _hover={id ? { bg: colors.uploadHover } : undefined}
                          borderLeftWidth="4px"
                          borderLeftColor={thread.unread_count > 0 ? 'blue.400' : 'transparent'}
                          onClick={() => {
                            if (id && !isExpanded) void openEmail(id)
                            toggleThreadExpansion(threadKey)
                          }}
                        >
                          <Avatar
                            name={name}
                            size="sm"
                            bg={getAvatarColorFromName(name)}
                            color="white"
                          />
                          <Box flex="1" minW={0}>
                            <Flex justify="space-between" align="center" gap="10px">
                              <Text fontWeight="700" fontSize="sm" color={colors.heading} noOfLines={1}>
                                {thread.thread_subject || '(No subject)'}
                              </Text>
                              <Text fontSize="xs" color={colors.textMuted} whiteSpace="nowrap">
                                {formatReceivedShort(thread.latest_at)}
                              </Text>
                            </Flex>
                            <Flex justify="space-between" align="center" mt="6px" gap="10px">
                              <Text fontSize="sm" color={colors.textSecondary} noOfLines={1}>
                                {name}
                              </Text>
                              <Badge colorScheme="gray" fontSize="0.65rem">
                                {thread.email_count}
                              </Badge>
                            </Flex>
                            <Flex justify="space-between" align="center" mt="6px" gap="10px">
                              <Text fontSize="sm" color={colors.textSecondary} noOfLines={1}>
                                {thread.preview || '—'}
                              </Text>
                              <UnreadBadge count={thread.unread_count} />
                            </Flex>
                            <Text mt="6px" fontSize="xs" color={colors.textMuted}>
                              {isExpanded ? 'Hide messages' : 'Show messages'}
                            </Text>
                          </Box>
                        </Flex>

                        {isExpanded && (
                          <VStack spacing={0} align="stretch" pl="36px" pb="8px">
                            {thread.emails.map((threadEmail) => {
                              const threadEmailName = senderLabel(threadEmail)
                              return (
                                <Flex
                                  key={threadEmail.id}
                                  px="12px"
                                  py="10px"
                                  cursor="pointer"
                                  bg={selectedId === threadEmail.id ? colors.uploadHover : 'transparent'}
                                  _hover={{ bg: colors.uploadHover }}
                                  borderLeftWidth="2px"
                                  borderLeftColor={threadEmail.is_read ? 'transparent' : 'blue.300'}
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    void openEmail(threadEmail.id)
                                  }}
                                >
                                  <Box minW={0} w="full">
                                    <Flex justify="space-between" align="center" gap="10px">
                                      <Text fontSize="xs" fontWeight="600" color={colors.text} noOfLines={1}>
                                        {threadEmailName}
                                      </Text>
                                      <Text fontSize="xs" color={colors.textMuted} whiteSpace="nowrap">
                                        {formatReceivedShort(threadEmail.received_at)}
                                      </Text>
                                    </Flex>
                                    <Text mt="4px" fontSize="xs" color={colors.textSecondary} noOfLines={1}>
                                      {threadEmail.subject || '(No subject)'}
                                    </Text>
                                  </Box>
                                </Flex>
                              )
                            })}
                          </VStack>
                        )}
                      </Box>
                    )
                  })}
                </VStack>
              )
            ) : emails.length === 0 ? (
              <Text color={colors.textMuted} fontSize="sm" px={4} py={6}>
                No emails yet.
              </Text>
            ) : (
              <VStack spacing={0} align="stretch">
                {emails.map((email) => {
                  const name = senderLabel(email)
                  return (
                    <Flex
                      key={email.id}
                      px="14px"
                      py="14px"
                      gap="12px"
                      cursor="pointer"
                      bg={selectedId === email.id ? colors.uploadHover : colors.cardBg}
                      _hover={{ bg: colors.uploadHover }}
                      borderBottom="1px solid"
                      borderColor={colors.borderLight}
                      borderLeftWidth="4px"
                      borderLeftColor={email.is_read ? 'transparent' : 'blue.400'}
                      onClick={() => void openEmail(email.id)}
                    >
                      <Avatar
                        name={name}
                        size="sm"
                        bg={getAvatarColorFromName(name)}
                        color="white"
                      />
                      <Box flex="1" minW={0}>
                        <Flex justify="space-between" align="center" gap="10px">
                          <Text
                            fontWeight={email.is_read ? '500' : '700'}
                            fontSize="sm"
                            color={colors.text}
                            noOfLines={1}
                          >
                            {name}
                          </Text>
                          <Text
                            fontSize="xs"
                            color={colors.textMuted}
                            whiteSpace="nowrap"
                          >
                            {formatReceivedShort(email.received_at)}
                          </Text>
                        </Flex>
                        <Text
                          fontSize="sm"
                          fontWeight="600"
                          color={colors.heading}
                          noOfLines={1}
                          mt="6px"
                        >
                          {email.subject || '(No subject)'}
                        </Text>
                        <Flex justify="space-between" align="center" mt="6px" gap="10px">
                          <Text
                            fontSize="sm"
                            color={colors.textSecondary}
                            noOfLines={1}
                          >
                            {email.body_preview || '—'}
                          </Text>
                          <UnreadBadge count={email.is_read ? 0 : 1} />
                        </Flex>
                      </Box>
                    </Flex>
                  )
                })}
                {emailsLoadingMore ? (
                  <Flex justify="center" py={4}>
                    <Spinner size="sm" />
                  </Flex>
                ) : null}
                {!emailsHasMore && emails.length > 0 ? (
                  <Text color={colors.textMuted} fontSize="xs" textAlign="center" py={4}>
                    End of inbox list.
                  </Text>
                ) : null}
              </VStack>
            )}
          </Box>
        </GridItem>

        <GridItem minW={0} minH={0} overflow="hidden" bg={colors.cardBg}>
          <Grid templateRows="1fr" h="full" minW={0} minH={0}>
            <GridItem minW={0} minH={0} overflow="hidden" display="flex" flexDir="column">
              {!selectedId && (
                <Box p="24px">
                  <Text color={colors.textMuted}>Select a thread to view messages.</Text>
                </Box>
              )}
              {selectedId && detailLoading && (
                <Flex justify="center" align="center" flex="1" minH="120px">
                  <Spinner />
                </Flex>
              )}
              {selectedId && !detailLoading && selectedEmail && (
                <Box
                  flex="1"
                  minH={0}
                  display="flex"
                  flexDir="column"
                  overflow="hidden"
                >
                  <Box
                    flex="1"
                    minH={0}
                    overflowY="auto"
                    overscrollBehaviorY="contain"
                    ref={detailScrollRef}
                  >
                    <Box ref={bodyTopRef} />
                    <Flex px="16px" py="14px" gap="10px" align="flex-start">
                      <Avatar
                        name={senderLabel(selectedEmail)}
                        size="sm"
                        bg={getAvatarColorFromName(senderLabel(selectedEmail))}
                        color="white"
                      />
                      <Box flex="1" minW={0}>
                        <Flex justify="space-between" align="center" gap="10px">
                          <Flex align="baseline" gap="10px" minW={0} flexWrap="wrap">
                            <Text fontWeight="700" color={colors.heading} noOfLines={1}>
                              {senderLabel(selectedEmail)}
                            </Text>
                            {selectedEmail.from_email ? (
                              <Text
                                fontSize="sm"
                                color={colors.textMuted}
                                noOfLines={1}
                              >
                                {selectedEmail.from_email}
                              </Text>
                            ) : null}
                          </Flex>
                          <Text
                            fontSize="sm"
                            color={colors.textMuted}
                            whiteSpace="nowrap"
                          >
                            {formatReceivedFull(selectedEmail.received_at)}
                          </Text>
                        </Flex>
                        <Heading size="sm" mt="10px" color={colors.heading}>
                          {selectedEmail.subject || '(No subject)'}
                        </Heading>
                        <Divider borderColor={colors.borderLight} my="12px" />
                        <Flex justify="space-between" align="center" gap={2} wrap="wrap" mb="12px">
                          <HStack spacing={2}>
                            <Button
                              size="sm"
                              variant="outline"
                              leftIcon={<Reply size={15} />}
                              isDisabled={!selectedCanReply}
                              onClick={() => {
                                setComposerMode('reply')
                                setReplyBody(buildQuotedReply(selectedEmail))
                                setReplyAttachments([])
                                setReplyCc('')
                                setForwardTo('')
                              }}
                            >
                              Reply
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              leftIcon={<ReplyAll size={15} />}
                              isDisabled={!selectedCanReply}
                              onClick={() => {
                                setComposerMode('replyAll')
                                setReplyBody(buildQuotedReply(selectedEmail))
                                setReplyAttachments([])
                                setReplyCc('')
                                setForwardTo('')
                              }}
                            >
                              Reply all
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              leftIcon={<Forward size={15} />}
                              onClick={() => {
                                setComposerMode('forward')
                                setReplyBody(buildQuotedReply(selectedEmail))
                                setReplyAttachments([])
                                setReplyCc('')
                                setForwardTo('')
                              }}
                            >
                              Forward
                            </Button>
                          </HStack>
                          {!selectedEmail.is_read ? (
                            <Button size="sm" colorScheme="blue" onClick={() => void handleMarkRead()}>
                              Mark as read
                            </Button>
                          ) : (
                            <Text fontSize="sm" color={colors.textMuted}>
                              This message is marked as read.
                            </Text>
                          )}
                        </Flex>
                        {composerOpen && (
                          <Box
                            borderWidth="1px"
                            borderColor={colors.border}
                            borderRadius="md"
                            p={3}
                            bg={colors.cardBg}
                            mb={3}
                          >
                            <Text fontSize="xs" color={colors.textMuted} mb={1}>
                              To
                            </Text>
                            {composerMode === 'forward' ? (
                              <Input
                                size="sm"
                                value={forwardTo}
                                onChange={(e) => setForwardTo(e.target.value)}
                                placeholder="recipient@example.com"
                                mb={2}
                              />
                            ) : (
                              <Input size="sm" value={selectedEmail.from_email || ''} isReadOnly mb={2} />
                            )}
                            <Text fontSize="xs" color={colors.textMuted} mb={1}>
                              Cc
                            </Text>
                            <Input
                              size="sm"
                              value={replyCc}
                              onChange={(e) => setReplyCc(e.target.value)}
                              placeholder="email@example.com, another@example.com"
                              mb={2}
                            />
                            <Text fontSize="xs" color={colors.textMuted} mb={1}>
                              Subject
                            </Text>
                            <Input
                              size="sm"
                              value={composerMode === 'forward' ? forwardSubject : replySubject}
                              isReadOnly
                              mb={2}
                            />
                            <Box mb={3}>
                              <EmailRichComposer
                                value={replyBody}
                                onChange={setReplyBody}
                                attachments={replyAttachments}
                                onAttachmentsChange={setReplyAttachments}
                                placeholder="Write your message…"
                                minHeight="160px"
                                isDisabled={replySending}
                              />
                            </Box>
                            <HStack spacing={2}>
                              <Button
                                size="sm"
                                colorScheme="blue"
                                onClick={() => void handleSendReply()}
                                isLoading={replySending}
                                isDisabled={isEmptyHtml(replyBody) || (composerMode === 'forward' && !forwardTo.trim())}
                              >
                                Send
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  setComposerMode(null)
                                  setReplyBody('')
                                  setReplyAttachments([])
                                  setReplyCc('')
                                  setForwardTo('')
                                }}
                              >
                                Cancel
                              </Button>
                            </HStack>
                          </Box>
                        )}
                        <Box
                          ref={emailBodyBoxRef}
                          borderWidth="1px"
                          borderColor={colors.border}
                          borderRadius="md"
                          overflow="hidden"
                          minH={`${emailBodyHeight}px`}
                        >
                          {selectedEmail.body ? (
                            <iframe
                              title="Email body"
                              sandbox="allow-same-origin"
                              srcDoc={normalizeEmailHtml(selectedEmail.body)}
                              style={{
                                width: '100%',
                                minHeight: `${emailBodyHeight}px`,
                                height: `${emailBodyHeight}px`,
                                border: 'none',
                                background: 'white',
                              }}
                            />
                          ) : (
                            <Text p={4} color={colors.textMuted} fontSize="sm">
                              No body content.
                            </Text>
                          )}
                        </Box>
                      </Box>
                    </Flex>
                  </Box>
                </Box>
              )}
            </GridItem>

          </Grid>
        </GridItem>
      </Grid>
    </Box>
  )
}
